import { useState, useEffect, useMemo } from 'react';
import { useFormContext, useFormState, Controller } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { data as currencyData } from 'currency-codes';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Button from 'components/UI/Buttons/BaseButton';
import InputLabel from 'components/UI/Inputs/InputLabel';
import TextField from 'components/UI/Inputs/TextField';
import FormControl from 'components/UI/Inputs/FormControl';
import Select from 'components/UI/Inputs/Select';
import Checkbox from 'components/UI/Inputs/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import clsx from 'clsx';

import LogoInput from 'src/features/proposals/ProposalForm/subForms/SettingsSubForm/Inputs/LogoInput';
import CustomerDetailsInput from 'src/features/proposals/ProposalForm/subForms/SettingsSubForm/Inputs/CustomerDetailsInput/CustomerDetailsInput';
import TaxRateInput from 'src/features/proposals/ProposalForm/subForms/SettingsSubForm/Inputs/TaxRateInput';
import styles from '../../ProformaInvoiceForm.module.scss';
import { FormValues } from './useSettingsSubForm';
import { useAppSelector } from 'src/app/hooks';
import { selectIsAdminOrOwner } from 'src/slices/workspacesSlice';

interface Props {
  onSubmit: (values: FormValues) => Promise<void> | void;
}

export default function SettingsLayout({ onSubmit }: Props) {
  const { t } = useTranslation(['proposal-settings', 'duplicates', 'proforma-invoices-page']);
  const isAdmin = useAppSelector(selectIsAdminOrOwner);

  const { handleSubmit, control } = useFormContext<FormValues>();

  const { isDirty, isSubmitting } = useFormState({ control });

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio === 0) setIsSticky(true);
        else if (entries[0].intersectionRatio === 1) setIsSticky(false);
      },
      { threshold: [0, 1] }
    );

    observer.observe(document.querySelector('#invoice-settings-bottom-marker'));

    return () => observer.disconnect();
  }, []);

  const currencyOptions = useMemo(
    () =>
      currencyData.map(({ code, currency }) => ({
        value: code,
        text: `${code} (${currency})`,
      })),
    []
  );

  const includeTotalPriceFieldId = 'invoice_settings_include_total_price';

  return (
    <form className={styles.settingsFormWrapper} onSubmit={handleSubmit(onSubmit)} noValidate>
      {!isAdmin && (
        <div className={styles.noPermissionOverlay}>
          <div className={styles.noPermissionContent}>
            <span>*</span> {t('proposal-settings:no_permission')}
          </div>
        </div>
      )}

      <LogoInput />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proposal-settings:form.field.businessName.label')}
                placeholder={t('proposal-settings:form.field.businessName.placeholder')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('duplicates:Email_field')}
                placeholder={t('duplicates:Email_field')}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('duplicates:Phone_number_field')}
                placeholder={t('duplicates:Phone_number_field')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="fax"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('proposal-settings:form.field.fax.label')}
                placeholder={t('duplicates:Phone_number_field')}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proposal-settings:form.field.description.label')}
                placeholder={t('proposal-settings:form.field.description.placeholder')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="website"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('duplicates:Website_field')}
                placeholder={t('proposal-settings:form.field.website.placeholder')}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proposal-settings:form.field.address.label')}
                placeholder={t('proposal-settings:form.field.address.placeholder')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="moreDetails"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proposal-settings:form.field.moreDetails.label')}
                placeholder={t('proposal-settings:form.field.moreDetails.placeholder')}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <h3 className={styles.sectionTitle}>
            {t('proforma-invoices-page:form.settings.paymentDetails.title')}
          </h3>
          <p className={styles.note}>
            {t('proforma-invoices-page:form.settings.paymentDetails.hint')}
          </p>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="bankName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proforma-invoices-page:form.settings.paymentDetails.bankName')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="bankAccountHolder"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proforma-invoices-page:form.settings.paymentDetails.bankAccountHolder')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="bankAccountNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proforma-invoices-page:form.settings.paymentDetails.bankAccountNumber')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="bankSwift"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proforma-invoices-page:form.settings.paymentDetails.bankSwift')}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Controller
            name="paymentInstructions"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proforma-invoices-page:form.settings.paymentDetails.paymentInstructions')}
                fullWidth
                multiline
                rows={3}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TaxRateInput />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel htmlFor="invoice_settings_currency">
                  {t('duplicates:Quote_settings_currency')}
                </InputLabel>
                <Select
                  {...field}
                  inputProps={{ id: 'invoice_settings_currency' }}
                  options={currencyOptions}
                  fullWidth
                />
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="includesTax"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('proforma-invoices-page:form.settings.includesTax')}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="includeTotalPrice"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel htmlFor={includeTotalPriceFieldId}>
                  {t('proposal-settings:form.field.includeTotalPrice.label')}
                </InputLabel>
                <Select
                  {...field}
                  id={includeTotalPriceFieldId}
                  value={Number(field.value)}
                  onChange={(event) => field.onChange(event.target.value === '1')}
                  options={[
                    {
                      value: 0,
                      text: t('proposal-settings:form.field.includeTotalPrice.value.dontInclude'),
                    },
                    {
                      value: 1,
                      text: t('proposal-settings:form.field.includeTotalPrice.value.include'),
                    },
                  ]}
                  fullWidth
                />
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="customerDetails"
            control={control}
            render={({ field }) => (
              <CustomerDetailsInput value={field.value} onChange={field.onChange} />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="footerText"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('proposal-settings:form.field.footerText.label')}
                placeholder={t('proposal-settings:form.field.footerText.placeholder')}
                fullWidth
                multiline
              />
            )}
          />
        </Grid>
      </Grid>

      <Box className={clsx(styles.settingsActions, { [styles.stickyActions]: isSticky })} mt={4}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isDirty || isSubmitting}
        >
          {t('proforma-invoices-page:form.actions.saveSettings')}
        </Button>
      </Box>
      <div id="invoice-settings-bottom-marker" style={{ height: 1 }} />
    </form>
  );
}
